import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Plus, Trash2, Save } from "lucide-react";

const DAYS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

export default function AvailabilitySettings() {
  const [cfg, setCfg] = useState(null);
  const [weekly, setWeekly] = useState([]);
  const [exceptions, setExceptions] = useState([]);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    const [{ data: c }, { data: a }] = await Promise.all([
      api.get("/advisor/scheduling-config"),
      api.get("/advisor/availability"),
    ]);
    setCfg(c || {});
    setWeekly(a.weekly || []);
    setExceptions(a.exceptions || []);
  };
  useEffect(() => { load(); }, []);

  const saveConfig = async () => {
    setBusy(true);
    try {
      await api.put("/advisor/scheduling-config", cfg);
      toast.success("Scheduling config saved");
    } catch (err) { toast.error("Failed"); } finally { setBusy(false); }
  };

  const saveAvailability = async () => {
    setBusy(true);
    try {
      await api.put("/advisor/availability", { weekly, exceptions });
      toast.success("Availability saved");
      load();
    } catch (err) { toast.error("Failed"); } finally { setBusy(false); }
  };

  const addRange = (dow) => setWeekly([...weekly, { id: crypto.randomUUID(), day_of_week: dow, start_time: "10:00", end_time: "13:00", timezone: cfg?.timezone || "Asia/Kolkata" }]);
  const rmRange = (idx) => setWeekly(weekly.filter((_, i) => i !== idx));
  const updRange = (idx, k, v) => setWeekly(weekly.map((w, i) => i === idx ? { ...w, [k]: v } : w));

  const addException = () => setExceptions([...exceptions, { id: crypto.randomUUID(), date: new Date().toISOString().slice(0, 10), is_blocked: true, custom_start_time: null, custom_end_time: null }]);
  const rmException = (idx) => setExceptions(exceptions.filter((_, i) => i !== idx));
  const updException = (idx, k, v) => setExceptions(exceptions.map((e, i) => i === idx ? { ...e, [k]: v } : e));

  if (!cfg) return <div className="p-10 text-center text-muted-foreground">Loading…</div>;

  return (
    <div className="space-y-8">
      <div>
        <p className="overline mb-1 text-muted-foreground">Settings</p>
        <h1 className="font-dashboard text-3xl font-bold tracking-tight">Discovery Call availability</h1>
        <p className="mt-1 text-sm text-muted-foreground">Configure the recurring weekly hours, slot duration and buffer that clients see when they book a Discovery Call.</p>
      </div>

      <Card className="border-border bg-white p-6" data-testid="config-card">
        <h2 className="mb-4 font-dashboard text-lg font-bold">Meeting rules</h2>
        <div className="grid gap-4 md:grid-cols-5">
          <div><Label>Slot length (min)</Label><Input type="number" value={cfg.slot_length_minutes || 30} onChange={(e) => setCfg({ ...cfg, slot_length_minutes: Number(e.target.value) })} data-testid="cfg-slot" /></div>
          <div><Label>Buffer (min)</Label><Input type="number" value={cfg.buffer_minutes || 15} onChange={(e) => setCfg({ ...cfg, buffer_minutes: Number(e.target.value) })} data-testid="cfg-buffer" /></div>
          <div><Label>Booking horizon (days)</Label><Input type="number" value={cfg.booking_horizon_days || 15} onChange={(e) => setCfg({ ...cfg, booking_horizon_days: Number(e.target.value) })} data-testid="cfg-horizon" /></div>
          <div><Label>Min notice (hours)</Label><Input type="number" value={cfg.min_notice_hours || 5} onChange={(e) => setCfg({ ...cfg, min_notice_hours: Number(e.target.value) })} data-testid="cfg-min-notice" /></div>
          <div><Label>Timezone</Label><Input value={cfg.timezone || "Asia/Kolkata"} onChange={(e) => setCfg({ ...cfg, timezone: e.target.value })} data-testid="cfg-tz" /></div>
        </div>
        <Button className="mt-4 gap-2" onClick={saveConfig} disabled={busy} data-testid="save-cfg-btn"><Save className="h-4 w-4" /> Save rules</Button>
      </Card>

      <Card className="border-border bg-white p-6" data-testid="weekly-card">
        <h2 className="mb-4 font-dashboard text-lg font-bold">Recurring weekly hours</h2>
        <div className="space-y-4">
          {DAYS.map((d, dow) => {
            const dayRanges = weekly.map((w, i) => ({ ...w, idx: i })).filter((w) => w.day_of_week === dow);
            return (
              <div key={dow} className="rounded-md border border-border p-4" data-testid={`day-${d.toLowerCase()}`}>
                <div className="mb-2 flex items-center justify-between">
                  <div className="font-dashboard text-sm font-bold">{d}</div>
                  <Button size="sm" variant="outline" onClick={() => addRange(dow)} data-testid={`add-range-${d.toLowerCase()}`}>
                    <Plus className="mr-1 h-3 w-3" /> Add range
                  </Button>
                </div>
                {dayRanges.length === 0 ? (
                  <p className="text-xs text-muted-foreground">Unavailable</p>
                ) : (
                  <div className="space-y-2">
                    {dayRanges.map((w) => (
                      <div key={w.idx} className="flex items-center gap-2">
                        <Input type="time" value={w.start_time} onChange={(e) => updRange(w.idx, "start_time", e.target.value)} className="w-28" />
                        <span className="text-muted-foreground">to</span>
                        <Input type="time" value={w.end_time} onChange={(e) => updRange(w.idx, "end_time", e.target.value)} className="w-28" />
                        <Button size="sm" variant="ghost" onClick={() => rmRange(w.idx)}><Trash2 className="h-3 w-3" /></Button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </Card>

      <Card className="border-border bg-white p-6">
        <div className="mb-4 flex items-center justify-between">
          <h2 className="font-dashboard text-lg font-bold">Blocked dates / exceptions</h2>
          <Button size="sm" variant="outline" onClick={addException} data-testid="add-exception-btn"><Plus className="mr-1 h-3 w-3" /> Add exception</Button>
        </div>
        {exceptions.length === 0 ? <p className="text-sm text-muted-foreground">None.</p> : (
          <div className="space-y-3">
            {exceptions.map((e, i) => (
              <div key={e.id || i} className="grid gap-3 rounded-md border border-border p-3 md:grid-cols-5">
                <Input type="date" value={e.date} onChange={(ev) => updException(i, "date", ev.target.value)} />
                <select className="h-10 rounded-md border border-input bg-background px-3 text-sm" value={e.is_blocked ? "block" : "custom"} onChange={(ev) => updException(i, "is_blocked", ev.target.value === "block")}>
                  <option value="block">Blocked (unavailable)</option>
                  <option value="custom">Custom hours</option>
                </select>
                {!e.is_blocked && <>
                  <Input type="time" value={e.custom_start_time || ""} onChange={(ev) => updException(i, "custom_start_time", ev.target.value)} />
                  <Input type="time" value={e.custom_end_time || ""} onChange={(ev) => updException(i, "custom_end_time", ev.target.value)} />
                </>}
                <div className={"flex " + (e.is_blocked ? "col-span-3 justify-end" : "justify-end")}>
                  <Button size="sm" variant="ghost" onClick={() => rmException(i)}><Trash2 className="h-3 w-3" /></Button>
                </div>
              </div>
            ))}
          </div>
        )}
      </Card>

      <Button className="gap-2" onClick={saveAvailability} disabled={busy} data-testid="save-availability-btn">
        <Save className="h-4 w-4" /> Save weekly hours & exceptions
      </Button>
    </div>
  );
}
